import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { FirestoreProductBisacSubject } from "reedy-data/models";
import { BasePanel } from "components/BasePanel";
import { EmptyList } from "components/empty/EmptyList";
import { SelectableListItem } from "components/List/SelectableListItem";
import { Text } from "components/text";

interface DatabaseBisacCodesProps {
  subjects: FirestoreProductBisacSubject[];
  isFinal: (subject: FirestoreProductBisacSubject) => boolean;
  onAdd: (subject: FirestoreProductBisacSubject) => void;
  onRemove: (subject: FirestoreProductBisacSubject) => void;
}

export const DatabaseBisacCodes = ({
  subjects,
  isFinal,
  onAdd,
  onRemove,
}: DatabaseBisacCodesProps) => {
  const { t } = useTranslation(["productDetails"]);

  return (
    <BasePanel
      title={t("productDetails:bisac.database.title")}
      className="flex-1 max-w-160 max-h-full"
      headerAction={
        <span className="flex items-center gap-1 text-secondary-500">
          <Lock className="size-3.5" />
          <Text as="span" variant="caption">
            {t("productDetails:bisac.database.readOnly")}
          </Text>
        </span>
      }
      isEmpty={!subjects.length}
      emptyComponent={
        <EmptyList
          title={t("productDetails:bisac.database.empty.title")}
          text={t("productDetails:bisac.database.empty.description")}
        />
      }
    >
      {subjects.map((subject) => {
        const isSelected = isFinal(subject);
        return (
          <SelectableListItem
            key={subject.id}
            id={subject.id}
            text={subject.subjectCode ?? ""}
            subtext={subject.subjectHeadingText ?? ""}
            isSelected={isSelected}
            testId={`database-bisac-${subject.subjectCode}`}
            onSelect={() => (isSelected ? onRemove(subject) : onAdd(subject))}
            className="px-5"
            textClassName="w-22 shrink-0 text-left"
            subtextVariant="muted"
          />
        );
      })}
    </BasePanel>
  );
};
